import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Users2, ArrowRight } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import api from "../../lib/api";

const money = (n, ccy) => `${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${ccy}`;

const CAT_META = {
  transportation: ["Transportation", "#0ea5e9"], accommodation: ["Accommodation", "#6366f1"],
  food: ["Food & Drink", "#f59e0b"], activities: ["Activities", "#10b981"],
  shopping: ["Shopping", "#ec4899"], health: ["Health", "#ef4444"],
  communication: ["Communication", "#8b5cf6"], travel_services: ["Travel Services", "#14b8a6"],
  financial: ["Fees & Financial", "#64748b"], other: ["Other", "#a8a29e"],
};

/* Where the money goes — one pie per home currency (usually just one). */
function CategoryPie({ ccy, cats }) {
  const data = Object.entries(cats)
    .map(([id, v]) => ({ id, name: CAT_META[id]?.[0] || "Other", value: v, color: CAT_META[id]?.[1] || "#a8a29e" }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);
  if (!data.length) return null;
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="border border-zinc-300 bg-white p-4" data-testid={`hub-expenses-pie-${ccy}`}>
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Where the money goes ({ccy})</div>
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <div className="w-44 h-44 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={38} outerRadius={70}
                paddingAngle={2} stroke="#fff" strokeWidth={1} isAnimationActive={false}>
                {data.map((d) => <Cell key={d.id} fill={d.color} />)}
              </Pie>
              <Tooltip formatter={(v, name) => [money(v, ccy), name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {data.map((d) => (
            <div key={d.id} className="flex items-center gap-2 text-xs" data-testid={`hub-expenses-cat-${d.id}`}>
              <span className="w-2.5 h-2.5 shrink-0" style={{ background: d.color }} />
              <span className="flex-1 truncate">{d.name}</span>
              <span className="font-mono">{money(d.value, ccy)}</span>
              <span className="text-muted-foreground w-9 text-right">{Math.round(d.value / total * 100)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* My Travel Hub — Expense Tracker tab: spend across every trip (own + shared),
   budget usage bars and one-click links into each trip's full expense tracker. */
export default function HubExpenses() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/expenses/overview").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  return (
    <section data-testid="hub-expenses-section">
      <h2 className="font-heading font-900 text-[#b45309] text-2xl mb-1 flex items-center gap-2">
        <Wallet className="w-6 h-6" /> Expense Tracker
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Spending across all your trips — including trips shared with you. Open any trip to add expenses,
        scan receipts, set budgets and settle up with the family.{" "}
        <Link to="/expenses" data-testid="hub-expenses-manager-link" className="text-[#b45309] font-700 hover:underline">
          General Expense Manager (household & everyday) →
        </Link>
      </p>
      {data === null && <p className="text-sm text-muted-foreground">Loading your spending…</p>}
      {data?.error && (
        <p className="text-sm text-rose-700 border border-rose-200 bg-rose-50 p-4" data-testid="hub-expenses-error">
          Couldn't load your expenses right now — please refresh the page to try again.
        </p>
      )}
      {data?.trips?.length === 0 && (
        <p className="text-sm text-muted-foreground border border-zinc-200 bg-white p-4" data-testid="hub-expenses-empty">
          No expenses recorded yet — open a trip and add your first expense or scan a receipt.
        </p>
      )}
      {data?.trips?.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="hub-expenses-totals">
            {Object.entries(data.grand_totals).map(([ccy, v]) => (
              <span key={ccy} className="border border-amber-300 bg-amber-50 text-amber-900 px-3 py-1.5 text-sm font-700 font-mono">
                Total: {money(v, ccy)}
              </span>
            ))}
          </div>
          <div className="space-y-3 mb-4">
            {Object.entries(data.by_category || {}).map(([ccy, cats]) => <CategoryPie key={ccy} ccy={ccy} cats={cats} />)}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.trips.map((t) => (
              <Link key={t.trip_id} to={`/trips/${t.trip_id}#expenses`} data-testid={`hub-expense-trip-${t.trip_id}`}
                className="border border-zinc-300 bg-white p-4 hover:border-[#b45309] transition-colors group">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-700 text-sm truncate">{t.title}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {[t.start_date, (t.stops || []).join(" → ")].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {t.shared && <span className="inline-flex items-center gap-1 text-[10px] font-700 bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full shrink-0"><Users2 className="w-3 h-3" /> shared</span>}
                </div>
                <div className="font-mono font-900 text-xl mt-2">{money(t.total, t.home_currency)}</div>
                <div className="text-xs text-muted-foreground">{t.count} expense{t.count === 1 ? "" : "s"}</div>
                {t.budget_pct != null && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-zinc-100 overflow-hidden">
                      <div className={`h-full ${t.budget_pct > 100 ? "bg-rose-500" : t.budget_pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                        style={{ width: `${Math.min(100, t.budget_pct)}%` }} />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{t.budget_pct}% of budget {money(t.budget_total, t.home_currency)}</div>
                  </div>
                )}
                <div className="inline-flex items-center gap-1 text-xs font-700 text-[#b45309] mt-2 group-hover:underline">
                  Open expense tracker <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
